import React, { useContext, useEffect } from "react";
import { AuthContext } from "./AuthContext";
import I18n from "../../translations/i18n";
import { numOfHotels } from "../../settings/IntegrationConstants";
import translations from "../../hotel_integration_modules/utilities/translations";
import GuestbookDark from "../UI/Icons/GuestbookDark";
import CashBack from "../UI/Icons/CashBack";
import Hotel from "../UI/Icons/Hotel";
import Heart from "../UI/Icons/Heart";
import styled, { css } from "styled-components";
import Close from "../UI/Icons/Close";

type HoverOverMessageProps = {
  locale: string;
  show: boolean;
  coordinates: null | [number, number];
  messagingHeight: number;
  handleOpen: (modalSection: string) => void;
  hotelSettings: any;
  mobileTooltip: boolean;
  hoverOverSide: 'left' | 'right';
  setShowHoverOver: (a: boolean) => void;
};

type PopOverProps = {
  show: boolean;
  coordinates: null | [number, number];
  messagingHeight: number;
  mobileTooltip: boolean;
  hoverOverSide: 'left' | 'right';
};

type RewardTitleProp = {
  type: string;
};

const PopOver = styled.div<PopOverProps>`
  padding: 23px;
  text-align: center;
  display: ${({ show }) => (show ? "block;" : "none;")}
  position: absolute;
  color: #343638;
  background: #ffffff;
  box-sizing: border-box;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  ${({ coordinates }) => coordinates && `top: ${coordinates[1]}px;`};
  right: unset;
  bottom: unset;
  ${({ coordinates }) => coordinates && `left: ${coordinates[0]}px;`};
  width: 319px;
  ${({ hoverOverSide }) => hoverOverSide === 'left' ?
    `transform: translate(-103%, -50%);` :
    `transform: translate(3%, -50%);`};
  min-height: 272px;
  z-index: 10;
  outline: 0;

  &:after {
    z-index: 8;
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    margin-left: 0;
    ${({ messagingHeight }) =>
    messagingHeight && `top: calc(50% - ${messagingHeight / 2}px);`};
    ${({ hoverOverSide }) =>
    hoverOverSide === 'left' ?
      `right: 0; transform: translate(50%) rotate(225deg);` :
      `left: 0; transform: translate(-50%) rotate(45deg);`}
    box-sizing: border-box;
    border: 5px solid #fff;
    border-color: transparent transparent #fff #fff;
  }

  &:before {
    z-index: 9;
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    margin-left: 0;
    bottom: 0;
    ${({ messagingHeight }) =>
    messagingHeight && `top: calc(50% - ${messagingHeight / 2}px);`};
    ${({ hoverOverSide }) =>
    hoverOverSide === 'left' ?
      `right: 0; transform: translate(50%) rotate(225deg);` :
      `left: 0; transform: translate(-50%) rotate(45deg);`}
    box-sizing: border-box;
    background: #fff;
    border: 7px solid #fff;
    border-color: transparent transparent #fff #fff;
  }

  ${({ theme, mobileTooltip }) => !mobileTooltip && theme.tablet`
    display: none;
  `}

  ${({ theme, mobileTooltip }) => mobileTooltip && theme.tablet`
    top: 50%;
    left: 50%;
    transform: translateX(-50%); 
    position: fixed;

    &:after {
      display: none
    }

    &:before {
      display: none
    }
  `}

`;

const HoverOverHeader = styled.div`
  display: flex;
  margin: 0 0 12px 0;
  width: max-content;
`;

const HeaderText = styled.div`
  line-height: 18px;
  font-size: 18px;
  font-weight: 500;
  max-width: 258px;
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeaderTextCoBrand = styled.div`
  line-height: 22px;
  font-size: 19px;
  font-weight: 600;
  max-width: 258px;
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Reward = styled.div`
  margin-bottom: 13px;
`;

const RewardTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  margin-bottom: 5px;

  ${({ type }: RewardTitleProp) =>
    type === "link" &&
    css`
      margin-bottom: 0px;
      color: ${({ theme }) => theme.primaryColor};
    `}
`;

const SignUpLink = styled.span`
  color: ${({ theme }) => theme.primaryColor};
  text-decoration: underline;
  font-weight: 700;

  &:hover {
    cursor: pointer;
    font-weight: 800;
  }

  &:focus {
    font-weight: 800;
  }
`;

const NextStepsText = styled.div`
  color: ${({ theme }) => theme.primaryColor};
  font-size: 14px;
`;

const RewardDesc = styled.div`
  font-size: 13px;
`;

const LogoContainer = styled.div`
  width: 35px;
  height: 35px;
  position: relative;

  svg {
    height: 35px;
    width: 35px;
  }
`;

const IconContainer = styled.span`
  margin-right: 4px;
  position: relative;
  top: 3px;
  svg {
    path {
      fill: ${({ theme }) => theme.primaryColor};
    }
    display: inline !important;
    vertical-align: baseline !important;
    width: 16px;
    height: 16px;
  }
`;

const CloseIconContainer = styled.div`
  cursor: pointer;
  width: fit-content;
  position: absolute;
  top: 16px;
  right: 16px;

  & svg {
    height: 12px;
    width: 12px;
  }
`;

const HoverOverMessage = ({
  locale,
  show,
  coordinates,
  messagingHeight,
  handleOpen,
  hotelSettings,
  mobileTooltip,
  hoverOverSide,
  setShowHoverOver
}: HoverOverMessageProps) => {
  const authContext: any = useContext(AuthContext);
  const loggedIn = authContext?.logged_in ? true : false;
  const widgetLoginEnabled = hotelSettings.widget.loginEnabled;
  const whiteLabel = hotelSettings.hotel.whiteLabel;
  const cobrandingEnabled = hotelSettings.hotel.cobrandingEnabled;

  useEffect(() => {
    window.addEventListener('scroll', () => {
      setShowHoverOver(false);
    });
  }, []);

  const handleMouseOver = () => {
    setShowHoverOver(true);
  }

  const handleMouseOut = () => {
    setShowHoverOver(false);
  }

  const handleClose = () => {
    setShowHoverOver(false);
  }

  return (
    <PopOver
      id="gb-hoverover"
      tabIndex={0}
      show={show}
      coordinates={coordinates}
      messagingHeight={messagingHeight}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      mobileTooltip={mobileTooltip}
      hoverOverSide={hoverOverSide}
    >
      <div>
        {(cobrandingEnabled) ? (
          <HoverOverHeader>
            <HeaderTextCoBrand>
              {`${I18n.t(`views.hotel_integration.components.hover_over_messaging.title_cobrand`, {
                locale: locale,
                white_label: whiteLabel
              })}: `}
            </HeaderTextCoBrand>
            <CloseIconContainer onClick={handleClose}>
              <Close />
            </CloseIconContainer>

          </HoverOverHeader>
        ) : (
          <HoverOverHeader>
            <LogoContainer>
              <GuestbookDark />
            </LogoContainer>
            <HeaderText>
              {`${translations("hover_over_messaging", "title", locale)}: `}
            </HeaderText>
            <CloseIconContainer onClick={handleClose}>
              <Close />
            </CloseIconContainer>
          </HoverOverHeader>
        )}
        <Reward>
          <RewardTitle type="reward">
            <IconContainer>
              <CashBack />
            </IconContainer>
            {translations("hover_over_messaging", "cash_back.title", locale)}
          </RewardTitle>
          <RewardDesc>
            {translations(
              "hover_over_messaging",
              "cash_back.description",
              locale
            )}
          </RewardDesc>
        </Reward>
        <Reward>
          <RewardTitle type="reward">
            <IconContainer>
              <Heart />
            </IconContainer>
            {translations(
              "hover_over_messaging",
              "cash_for_a_cause.title",
              locale
            )}
          </RewardTitle>
          <RewardDesc>
            {translations(
              "hover_over_messaging",
              "cash_for_a_cause.description",
              locale
            )}
          </RewardDesc>
        </Reward>
        <Reward>
          <RewardTitle type="reward">
            <IconContainer>
              <Hotel />
            </IconContainer>
            {translations("hover_over_messaging", "cash_forward.title", locale)}
          </RewardTitle>
          <RewardDesc>
            {I18n.t(
              "views.hotel_integration.components.hover_over_messaging.cash_forward.description",
              {
                num_of_hotels: numOfHotels,
                locale: `${locale}`,
              }
            )}
          </RewardDesc>
        </Reward>
        {(!loggedIn && widgetLoginEnabled) ? (
          // LOGIN ENABLED, USER NOT LOGGED IN
          <RewardTitle type="link">
            <SignUpLink
              tabIndex={0}
              role="button"
              onClick={() => handleOpen("signup")}
              onKeyPress={() => handleOpen("signup")}
            >
              {translations("hover_over_messaging", "sign_up.link", locale)}
            </SignUpLink>
            &nbsp;
            {translations("hover_over_messaging", "sign_up.non_link", locale)}
          </RewardTitle>
        ) : (!loggedIn && !widgetLoginEnabled) ? (
          // LOGIN DISABLED, USER NOT LOGGED IN
          <NextStepsText>
            {translations("hover_over_messaging", "join_at_checkout", locale)}
          </NextStepsText>
        ) : (
          // USER LOGGED IN
          <></>
        )}
      </div>
    </PopOver>
  );
};

export default HoverOverMessage;
